import React from "react";
import styled from "styled-components";
import Text from "../../atoms/Text";
import Button from "../../atoms/Button";
import Tag from "../../atoms/Tag";
import { ConsultationRecordAdmin, ConsultationRecordStatus } from "../../../common/models/types";
import { getConsultationStatusLabel, getConsultationStatusColor } from "../../../api/consultationService";

interface Props {
  records: ConsultationRecordAdmin[];
  loading: boolean;
  currentPage: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
  onViewDetail: (id: number) => void;
}

const TableContainer = styled.div`
  background: #fff;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  width: 100%;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
`;

const Thead = styled.thead`
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
`;

const Tbody = styled.tbody``;

const Tr = styled.tr`
  border-bottom: 1px solid #e5e7eb;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: #f9fafb;
  }
`;

const Th = styled.th`
  padding: 12px 16px;
  text-align: left;
  font-weight: 600;
  font-size: 14px;
  color: #374151;
  white-space: nowrap;

  &:nth-child(1) { width: 6%; }   /* ID */
  &:nth-child(2) { width: 12%; }  /* 사용자 */
  &:nth-child(3) { width: 10%; }  /* 상태 */
  &:nth-child(4) { width: 32%; }  /* 기록지 미리보기 */
  &:nth-child(5) { width: 10%; }  /* 처리시간 */
  &:nth-child(6) { width: 10%; }  /* 비용 */
  &:nth-child(7) { width: 12%; }  /* 생성일 */
  &:nth-child(8) { width: 8%; }   /* 액션 */
`;

const Td = styled.td`
  padding: 12px 16px;
  font-size: 14px;
  color: #374151;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const PreviewText = styled.div`
  max-height: 60px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: pre-wrap;
  font-size: 13px;
  color: #6b7280;
  line-height: 1.4;
`;

const StatusBadge = styled.span<{ $color: string }>`
  display: inline-flex;
  align-items: center;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  color: #fff;
  background-color: ${props => props.$color};
`;

const EmptyState = styled.div`
  padding: 60px 20px;
  text-align: center;
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  padding: 20px;
  border-top: 1px solid #e5e7eb;
`;

const PageButton = styled.button<{ active?: boolean }>`
  padding: 6px 12px;
  border: 1px solid ${(props) => (props.active ? "#4A90E2" : "#e5e7eb")};
  background: ${(props) => (props.active ? "#4A90E2" : "#fff")};
  color: ${(props) => (props.active ? "#fff" : "#374151")};
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;

  &:hover:not(:disabled) {
    border-color: #4a90e2;
    color: ${(props) => (props.active ? "#fff" : "#4A90E2")};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

const PageInfo = styled(Text)`
  margin: 0 16px;
  font-size: 14px;
  color: #6b7280;
`;

// 처리 시간 포맷팅
const formatProcessingTime = (ms?: number): string => {
  if (!ms) return "-";
  const seconds = Math.floor(ms / 1000);
  if (seconds < 60) return `${seconds}초`;
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes}분 ${remainingSeconds}초`;
};

// 비용 포맷팅
const formatCost = (cost?: { total: number }): string => {
  if (!cost?.total) return "-";
  return `$${cost.total.toFixed(3)}`;
};

// 날짜 포맷팅
const formatDate = (dateStr?: string): string => {
  if (!dateStr) return "-";
  const date = new Date(dateStr);
  return date.toLocaleDateString("ko-KR", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export const AdminConsultationRecordTable: React.FC<Props> = ({
  records,
  loading,
  currentPage,
  pageSize,
  total,
  onPageChange,
  onViewDetail,
}) => {
  const totalPages = Math.ceil(total / pageSize);

  // 페이지 번호 생성
  const getPageNumbers = () => {
    const pages = [];
    const maxPagesToShow = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
    let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);

    if (endPage - startPage < maxPagesToShow - 1) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return pages;
  };

  if (loading && records.length === 0) {
    return (
      <TableContainer>
        <EmptyState>
          <Text>데이터를 불러오는 중...</Text>
        </EmptyState>
      </TableContainer>
    );
  }

  if (records.length === 0) {
    return (
      <TableContainer>
        <EmptyState>
          <Text>조회된 상담 기록이 없습니다.</Text>
        </EmptyState>
      </TableContainer>
    );
  }

  return (
    <TableContainer>
      <Table>
        <Thead>
          <Tr>
            <Th>ID</Th>
            <Th>사용자</Th>
            <Th>상태</Th>
            <Th>기록지 미리보기</Th>
            <Th>처리시간</Th>
            <Th>비용</Th>
            <Th>생성일</Th>
            <Th>액션</Th>
          </Tr>
        </Thead>
        <Tbody>
          {records.map((record) => (
            <Tr key={record.id}>
              <Td>{record.id}</Td>
              {/* 사용자 */}
              <Td>
                <div>
                  <Text textType="Caption" color="#374151">
                    {record.userName || "-"}
                  </Text>
                </div>
                <Text textType="Caption" color="#6b7280">
                  {record.userEmail || "-"}
                </Text>
              </Td>
              {/* 상태 */}
              <Td>
                <StatusBadge $color={getConsultationStatusColor(record.status)}>
                  {getConsultationStatusLabel(record.status)}
                </StatusBadge>
                {record.errorMessage && (
                  <div style={{ marginTop: 4 }}>
                    <Text textType="Caption" color="#ef4444">
                      {record.errorMessage.substring(0, 50)}
                      {record.errorMessage.length > 50 ? "..." : ""}
                    </Text>
                  </div>
                )}
              </Td>
              {/* 기록지 미리보기 */}
              <Td>
                <PreviewText>
                  {record.recordContentPreview || record.recordContent?.substring(0, 150) || "-"}
                </PreviewText>
              </Td>
              {/* 처리시간 */}
              <Td>
                {formatProcessingTime(record.analysis?.processingTimeMs)}
              </Td>
              {/* 비용 */}
              <Td>
                {formatCost(record.analysis?.cost)}
              </Td>
              {/* 생성일 */}
              <Td>
                {formatDate(record.createdAt)}
              </Td>
              {/* 액션 */}
              <Td>
                <Button
                  size="Small"
                  type="Text"
                  variant="Contained"
                  color="Primary"
                  onClick={() => onViewDetail(record.id)}
                >
                  상세
                </Button>
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>

      {totalPages > 1 && (
        <PaginationContainer>
          <PageButton onClick={() => onPageChange(currentPage - 1)} disabled={currentPage === 1}>
            이전
          </PageButton>

          {getPageNumbers().map((page) => (
            <PageButton key={page} active={page === currentPage} onClick={() => onPageChange(page)}>
              {page}
            </PageButton>
          ))}

          <PageInfo>
            {currentPage} / {totalPages} 페이지
          </PageInfo>

          <PageButton
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
          >
            다음
          </PageButton>
        </PaginationContainer>
      )}
    </TableContainer>
  );
};
